<template>
  <div
    class="grow relative"
    :class="{
      'h-[calc(100vh-3rem)] sm:h-[calc(100vh-3.5rem)]':
        v2LayoutEnabled && isTenant && !isFullscreen,
      'h-screen': v2LayoutEnabled && (!isTenant || isFullscreen),
      'min-h-screen': !v2LayoutEnabled,
      'grid grid-rows-[1fr_auto_60px] sm:grid-rows-[1fr_auto]': v2LayoutEnabled,
    }"
  >
    <div
      id="main-app-content"
      class="h-full grow grid grid-rows-[1fr_auto] relative"
      :class="
        v2LayoutEnabled &&
        !isOnboarding &&
        !isOnTenantRoute &&
        !isFullscreen &&
        !currentSessionInformation.initializingSession
          ? 'grid-cols-1 p-2 sm:p-4 bg-surface-200/70 dark:bg-surface-800'
          : ''
      "
    >
      <!-- chat -->
      <div
        v-if="
          isV2Ready &&
          !isOnboarding &&
          v2LayoutEnabled &&
          v2InputStore.state.key === 'DEFAULT' &&
          !currentSessionInformation.initializingSession &&
          !!currentSessionInformation.appAuthToken
        "
        class="flex items-center justify-center absolute inset-0"
      >
        <V2ResultOverlay
          class="w-full h-full absolute inset-0"
          data-html2canvas-ignore
          :class="{
            'top-[unset] left-[unset] right-0':
              v2ResponseStore.isResponseOverlayCollapsed,
          }"
        />
      </div>

      <div
        id="main-app-content"
        class="h-full grow flex flex-col relative col-span-1"
        :class="{
          'overflow-y-auto': v2LayoutEnabled,
          'border border-surface-200 dark:border-surface-700 rounded-md bg-gray-50 dark:bg-surface-900':
            v2LayoutEnabled && isCurrentRouteDisplayed,
          'shadow-xl':
            v2LayoutEnabled &&
            isCurrentRouteDisplayed &&
            !isFullscreen &&
            !isOnTenantRoute &&
            !currentSessionInformation.initializingSession,

          'sr-only':
            v2LayoutEnabled &&
            !isCurrentRouteDisplayed &&
            !isOnboarding &&
            !isFullscreen &&
            !isOnTenantRoute &&
            !currentSessionInformation.initializingSession,
        }"
      >
        <div class="absolute inset-0">
          <slot :is-v2-ready="isV2Ready" />
        </div>
      </div>

      <!-- v2 heading deprecated -->
      <div
        id="v2-sub-header"
        class="flex flex-col relative @container min-h-1.5 sr-only"
        :class="
          v2LayoutEnabled && !v2ResponseStore.isResponseOverlayCollapsed
            ? 'col-span-2'
            : ''
        "
      >
        <div
          v-if="
            v2LayoutEnabled &&
            !v2InputStore.state.hidden &&
            v2InputStore.state.key === 'DEFAULT' &&
            !currentSessionInformation.initializingSession
          "
          class="absolute bottom-full right-4 z-20"
        >
          <!-- <CollapsedConversation /> -->
        </div>
      </div>
      <!-- v2 heading end -->
    </div>

    <!-- resizable v2 bottom section -->
    <div class="relative z-10 @container empty:hidden">
      <V2BottomActionBar
        v-if="
          isV2Ready &&
          v2LayoutEnabled &&
          !isFullscreen &&
          !isOnTenantRoute &&
          isAuth &&
          !currentSessionInformation.initializingSession
        "
      />

      <!-- bottom section -->
      <AppBottomSection v-if="isV2Ready && v2LayoutEnabled && isAuth && !isFullscreen" />

      <!-- help tips start -->
      <div
        v-if="helpStore.currentContent && helpStore.inlineHelpOpen"
        class="h-px z-40 absolute bottom-full mb-3 left-0 w-full"
      >
        <div class="relative h-full w-full max-w-7xl mx-auto">
          <!-- <button class="h-10 w-10 rounded-full">
            <QuestionMarkCircleIcon class="h-6 w-6" />
          </button> -->
          <span
            v-if="isHelpCollapsed && isMobile"
            class="scale-125 absolute bottom-full left-4"
            @click="toggleHelpCollapsed()"
          >
            <AppButton icon="pi pi-question" rounded size="large" />
          </span>
          <div
            v-else-if="!isHelpCollapsed || !isMobile"
            class="absolute bottom-full w-full left-0 pl-2 sm:pl-0"
          >
            <InlineHelpMessage
              :data="helpStore.currentContent"
              class="ma-text-secondary bg-surface-300 dark:bg-surface-800 sm:bg-transparent dark:sm:bg-transparent"
              @close="
                isMobile ? toggleHelpCollapsed() : helpStore.toggleInlineHelp(false)
              "
            />
            <HandDrawnArrow
              class="h-20 w-20 z-40 translate-x-20 rotate-12 opacity-0 animate-[fade-in_0.5s_1s_ease-in_forwards] -mb-12"
            />
          </div>
        </div>
      </div>
      <!-- help tips end -->
    </div>

    <!-- V2 menu -->
    <teleport to="body">
      <div
        class="flex sm:flex-col gap-2 fixed items-center left-4 sm:left-0 sm:w-15.75 md:w-23.75 bottom-16 sm:bottom-6 z-55"
        :class="v2LayoutEnabled ? '' : 'sm:hidden'"
      >
        <V2MenuToggles @sign-out="router.push({ name: RouteNames.LOGOUT })" />
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import V2MenuToggles from "@/components/v2/V2MenuToggles.vue";
import InlineHelpMessage from "@/components/help/InlineHelpMessage.vue";
import HandDrawnArrow from "@/components/shared/HandDrawnArrow.vue";
import V2ResultOverlay from "@/components/v2/V2ResultOverlay.vue";
import V2BottomActionBar from "@/components/v2/V2BottomActionBar.vue";

const AppBottomSection = defineAsyncComponent(
  () => import("@/components/wrapper/AppBottomSection.vue")
);

defineProps<{
  isAuth: boolean;
  isFullscreen?: boolean;
  isOnTenantRoute?: boolean;
  isTenant?: boolean;
}>();

const helpStore = useAppHelpStore();
const { v2LayoutEnabled, isCurrentRouteDisplayed, toggleCurrentRoute } = useV2Layout();
const v2ResponseStore = useV2ResponseStore();
const router = useRouter();
const isV2Ready = ref(false);
const v2InputStore = useV2InputStore();
const [isHelpCollapsed, toggleHelpCollapsed] = useToggle(isMobile.value);

const isOnboarding = computed(
  () => router.currentRoute.value.name === RouteNames.ONBOARDING
);

const activeConversationId = computed(() => v2ResponseStore.lastConversationId);

router.afterEach((to, from) => {
  if (to.name === from.name && to.name !== RouteNames.BRAIN) return;
  if (!v2LayoutEnabled.value) return;
  if (to.meta.fullscreen) return;
  if (to.name === RouteNames.ONBOARDING) return;
  toggleCurrentRoute(true);
});

onMounted(() => {
  if (activeConversationId.value) {
    v2ResponseStore.showResponseOverlay();
  }
  nextTick(() => {
    isV2Ready.value = true;
  });
});
</script>

<!-- <style>
.image-tool__image {
  height: 400px;
  width: 400px;
}
</style> -->
